import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Image, Mic, File as FileIcon, Sparkles, Trash2, Eye, ClipboardList, Star } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { QuestionnaireDetailsModal } from "./QuestionnaireDetailsModal";
import { SatisfactionSurveyDetails } from "./SatisfactionSurveyDetails";

interface SubmissionMetadata {
  type?: string;
  responses?: {
    name?: string;
    sex?: string;
    mainReason?: string;
    overallRating?: number;
    wouldRecommend?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface Submission {
  id: string;
  link_id: string;
  patient_id: string;
  submission_type: string;
  file_path?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  text_content?: string | null;
  status: string;
  metadata?: SubmissionMetadata | null;
  created_at: string;
  processed_at?: string | null;
  processed_by?: string | null;
  smartfile_session_id?: string | null;
}

interface AssistedSubmissionCardProps {
  submission: Submission;
  onProcessed: () => void;
}

const typeIcons = {
  photo: Image,
  audio: Mic,
  file: FileIcon,
  text: FileText,
};

const typeLabels = {
  photo: "Foto",
  audio: "Áudio",
  file: "Arquivo",
  text: "Texto",
};

export function AssistedSubmissionCard({ submission, onProcessed }: AssistedSubmissionCardProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showQuestionnaireDetails, setShowQuestionnaireDetails] = useState(false);
  const [showSurveyDetails, setShowSurveyDetails] = useState(false);

  const Icon = typeIcons[submission.submission_type as keyof typeof typeIcons];
  const label = typeLabels[submission.submission_type as keyof typeof typeLabels];

  // Verificar se há metadata estruturado
  const metadata = submission.metadata || {};
  const isQuestionnaire = metadata.type === 'first_consultation';
  const isSurvey = metadata.type === 'satisfaction_survey';

  const handleProcess = async (action: 'approve' | 'reject') => {
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('process-assisted-submission', {
        body: { submission_id: submission.id, action }
      });

      if (error) throw error;

      if (action === 'approve' && data.smartfile_session_id) {
        // Navegar para Smartfile Step 2
        const patientId = submission.patient_id;
        navigate(
          `/app/smartfile?sessionId=${data.smartfile_session_id}&patientId=${patientId}&fromAssisted=true`
        );

        toast.success("Redirecionando para análise...");
      } else if (action === 'reject') {
        toast.success("Submissão descartada.");
        onProcessed();
      }
    } catch (error) {
      console.error('[AssistedSubmissionCard] Error:', error);
      toast.error(error instanceof Error ? error.message : "Erro ao processar submissão");
    } finally {
      setLoading(false);
      setShowRejectDialog(false);
    }
  };

  const handlePreview = async () => {
    if (submission.submission_type === 'text') {
      setShowPreview(true);
      return;
    }

    if (submission.file_path) {
      const { data } = await supabase.storage
        .from('assisted-submissions')
        .createSignedUrl(submission.file_path, 3600);

      if (data?.signedUrl) {
        setPreviewUrl(data.signedUrl);
        setShowPreview(true);
      }
    }
  };

  return (
    <>
      <Card className="border-l-4 border-l-orange-500">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Icon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>

            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">
                  {isQuestionnaire ? 'Questionário' : isSurvey ? 'Pesquisa de Satisfação' : label}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(submission.created_at), {
                    addSuffix: true,
                    locale: ptBR
                  })}
                </span>
              </div>

              {isQuestionnaire && metadata.responses && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Questionário de Primeiro Atendimento</p>
                  <p className="text-xs text-muted-foreground">
                    Nome: {metadata.responses.name || 'Não informado'} | 
                    Sexo: {metadata.responses.sex || 'Não informado'} | 
                    Motivo: {metadata.responses.mainReason ? metadata.responses.mainReason.substring(0, 50) + '...' : 'Não informado'}
                  </p>
                </div>
              )}

              {isSurvey && metadata.responses && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Pesquisa de Satisfação</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Nota Geral: {metadata.responses.overallRating || 0}/5</span>
                    <span>•</span>
                    <span>Recomendaria: {metadata.responses.wouldRecommend ? 'Sim' : 'Não'}</span>
                  </div>
                </div>
              )}

              {!isQuestionnaire && !isSurvey && submission.submission_type === 'text' && submission.text_content && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {submission.text_content}
                </p>
              )}

              {!isQuestionnaire && !isSurvey && submission.file_name && (
                <p className="text-sm font-medium">{submission.file_name}</p>
              )}

              {!isQuestionnaire && !isSurvey && submission.file_size && (
                <p className="text-xs text-muted-foreground">
                  {(submission.file_size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <div className="flex gap-2">
              {isQuestionnaire && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowQuestionnaireDetails(true)}
                >
                  <ClipboardList className="h-4 w-4 mr-1" />
                  Ver Detalhes
                </Button>
              )}
              {isSurvey && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSurveyDetails(true)}
                >
                  <Star className="h-4 w-4 mr-1" />
                  Ver Detalhes
                </Button>
              )}
              {!isQuestionnaire && !isSurvey && (submission.text_content || submission.file_path) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreview}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Ver
                </Button>
              )}
              {!isQuestionnaire && !isSurvey && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleProcess('approve')}
                  disabled={loading}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Analisar
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowRejectDialog(true)}
                disabled={loading}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diálogo de Confirmação de Rejeição */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar Submissão?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A submissão será marcada como descartada
              e não será processada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleProcess('reject')}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <AlertDialog open={showPreview} onOpenChange={setShowPreview}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Preview</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="max-h-96 overflow-auto">
            {submission.submission_type === 'text' && (
              <p className="whitespace-pre-wrap">{submission.text_content}</p>
            )}
            {submission.submission_type === 'photo' && previewUrl && (
              <img src={previewUrl} alt="Preview" className="w-full rounded-lg" />
            )}
            {submission.submission_type === 'audio' && previewUrl && (
              <audio src={previewUrl} controls className="w-full" />
            )}
            {submission.submission_type === 'file' && previewUrl && (
              <div className="w-full">
                <p className="text-sm text-muted-foreground mb-2">
                  Arquivo: {submission.file_name}
                </p>
                <iframe
                  src={previewUrl}
                  className="w-full h-96 rounded-lg border"
                  title="Preview do arquivo"
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Fechar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Detalhes do Questionário */}
      {isQuestionnaire && (
        <QuestionnaireDetailsModal
          open={showQuestionnaireDetails}
          onOpenChange={setShowQuestionnaireDetails}
          data={metadata.responses}
        />
      )}

      {/* Modal de Detalhes da Pesquisa */}
      {isSurvey && (
        <SatisfactionSurveyDetails
          open={showSurveyDetails}
          onOpenChange={setShowSurveyDetails}
          data={metadata.responses}
        />
      )}
    </>
  );
}
